import { BusinessRuleItemType } from '../constants/globals';
import { KnownClearingHouses } from './BillingCode.type';
import { ExchangeDispositionDataSource, IntegrationExchangeTypeEnum } from './Integration.type';

/**
 * Connector admin/ops shapes shared across the call chain (defensive typing):
 * connection-api routes produce them, the admin ops console consumes them.
 */

/**
 * Non-secret creation/validation audit for one IMMUTABLE named key, kept on
 * the platform row (`specifics.keyAudit[name]`). A key name maps to one
 * material forever — there is no rotation lifecycle; replacing material means
 * creating a NEW name and re-pointing `keyRouting`.
 */
export type ConnectorKeyAudit = {
  createdAt?: number;
  createdBy?: string;
  validatedAt?: number;
  validatedBy?: string;
  validatedResult?: 'valid' | 'invalid';
};

/**
 * What a named key's traffic IS: a vendor test-mode key never contacts real
 * payers, so its traffic is mock data on every stage; a production key's
 * traffic is treated as PHI everywhere. `phi` (the default) keeps today's
 * scrubbing; `mock` lets trace/operations detail persist unscrubbed.
 */
export type ConnectorKeyDataClass = 'phi' | 'mock';

/**
 * Non-secret per-key state on the platform row (`specifics.keyMetadata[name]`).
 * Nullable timestamps ARE the state: null/absent = not deprecated / never
 * probed. Who performed a write is the config audit row's job.
 */
export type ConnectorKeyMetadata = {
  /** Epoch ms the key was deprecated; null/absent = not deprecated. */
  deprecated?: number | null;
  dataClass?: ConnectorKeyDataClass;
  /**
   * Epoch ms the key was last PROVEN against the vendor — by the credential
   * check or the console's manual Validate; null/absent = never probed.
   */
  probed?: number | null;
  lastVerdict?: ConnectorCredentialVerdict;
};

/**
 * Key routing is by atomic FLOW, not raw exchange type (Brian, 2026-08-28):
 * a request and its response are one conversation — a 271 answered on a
 * different key than its 270 is meaningless, so the pair routes together.
 */
export type ConnectorRoutingGroupId =
  | 'claims'
  | 'eligibility'
  | 'registration'
  | 'enrollment'
  | 'poll'
  | 'payor-directory';

/**
 * Which flow each exchange type belongs to — TOTAL over the enum, so a new
 * exchange type does not compile until it declares its flow.
 */
export const EXCHANGE_TYPE_ROUTING_GROUP: Record<
  IntegrationExchangeTypeEnum,
  ConnectorRoutingGroupId
> = {
  [IntegrationExchangeTypeEnum.Claim]: 'claims',
  [IntegrationExchangeTypeEnum.ClaimAcknowledgment]: 'claims',
  [IntegrationExchangeTypeEnum.ImplementationAcknowledgment]: 'claims',
  [IntegrationExchangeTypeEnum.Remit]: 'claims',
  [IntegrationExchangeTypeEnum.File]: 'claims',
  [IntegrationExchangeTypeEnum.InsuranceEligibilityRequest]: 'eligibility',
  [IntegrationExchangeTypeEnum.InsuranceEligibilityResponse]: 'eligibility',
  [IntegrationExchangeTypeEnum.RegistrationRequest]: 'registration',
  [IntegrationExchangeTypeEnum.RegistrationResponse]: 'registration',
  [IntegrationExchangeTypeEnum.RegistrationCancellationRequest]: 'registration',
  [IntegrationExchangeTypeEnum.RegistrationCancellationResponse]: 'registration',
  [IntegrationExchangeTypeEnum.EnrollmentRequest]: 'enrollment',
  [IntegrationExchangeTypeEnum.EnrollmentResponse]: 'enrollment',
  [IntegrationExchangeTypeEnum.MetadataGet]: 'enrollment',
  [IntegrationExchangeTypeEnum.MetadataPut]: 'enrollment',
  [IntegrationExchangeTypeEnum.DocumentPut]: 'enrollment',
  [IntegrationExchangeTypeEnum.DocumentGet]: 'enrollment',
  [IntegrationExchangeTypeEnum.Poll]: 'poll',
  [IntegrationExchangeTypeEnum.PayorDirectory]: 'payor-directory',
};

/** The flow ids in display order (the routing matrix's columns after `default`). */
export const CONNECTOR_ROUTING_GROUP_IDS: readonly ConnectorRoutingGroupId[] = [
  'claims',
  'eligibility',
  'registration',
  'enrollment',
  'poll',
  'payor-directory',
];

/**
 * `specifics.keyRouting`: which named key serves which FLOW. `default` is
 * REQUIRED and covers every flow without an explicit entry — resolution fails
 * loud when the map or its `default` is missing.
 */
export type ConnectorKeyRouting = { default: string } & Partial<
  Record<ConnectorRoutingGroupId, string>
>;

/** One named key as the ops console sees it — never material. Presence in the map IS existence. */
export type ConnectorKeyStatus = {
  metadata: ConnectorKeyMetadata;
  audit: ConnectorKeyAudit;
};

/**
 * One input field of a manual operation as the console's form renderer
 * consumes it (derived server-side from the descriptor's Joi schema —
 * INFRA-11 Operations runner). Flat primitives only in v1.
 */
export type ConnectorOperationField = {
  name: string;
  type: string;
  required: boolean;
  /** Enum choices when the schema declares `valid(...)`. */
  allow?: (string | number | boolean)[];
  default?: string | number | boolean;
  description?: string;
};

/**
 * The Operations tab's list read: identity only. The field schema ships when
 * the operation is SELECTED (fetch=operation) — never on the list, never on
 * the console paint.
 */
export type ConnectorOperationListItem = Omit<ConnectorOperationSummary, 'fields' | 'prefill'>;

/** One vendor operation as the console's Operations page renders it. */
export type ConnectorOperationSummary = {
  name: string;
  title: string;
  exchangeType: IntegrationExchangeTypeEnum;
  /** True = changes vendor state; execution then requires `confirm: true`. */
  mutating: boolean;
  fields: ConnectorOperationField[];
  /**
   * When present, the console renders these inputs with a Fetch button: a
   * pure DB read that prefills the form from a house record (e.g. a
   * billingKey). Fetched values land in the EDITABLE fields — review, then
   * Run sends exactly what's shown.
   */
  prefill?: { fields: ConnectorOperationField[] };
};

/** What a prefill returns: field values for the form + a note for the operator. */
export type ConnectorOperationPrefillResult = {
  operation: string;
  values: Record<string, string | number | boolean>;
  note?: string;
};

/** Why a drain run stopped with feed remaining (reported, resumable — never silent). */
export type ConnectorDrainDeferral = 'page-cap' | 'time-budget';

/**
 * The latest noisy run's summary on a day-rolled `step='drain'` sink row
 * (`ConnectionApiActivity.data`). ONE shape for the producer
 * (`recordConnectorDrainActivity`), the drain cron, the ops console, and the
 * UI mock — the reader can never drift from the writer (an untyped `data`
 * cast is how the CAP DEFERRED tag silently broke in review).
 */
export type ConnectorDrainRunSummary = {
  polled: number;
  pages: number;
  passes: number;
  durationMs: number;
  deferred?: ConnectorDrainDeferral;
  /** Ingest outcome → count for the run (e.g. processed/unrouted/duplicate). */
  outcomes: Record<string, number>;
};

/** One row of GET /connector/vendors — the ops console's landing data. */
/**
 * Where a connector's code is executing: `local` = a developer's sls-offline
 * process, `deployed` = a real Lambda. Combined with the target database (the
 * STAGE the process connects to) this is the "combo" the polling toggle keys
 * on — e.g. local·dev, deployed·prod. Detected at runtime, never configured.
 */
export type ConnectorExecution = 'local' | 'deployed';

/**
 * Automation poll gate per execution location (plan §3f; Brian, 2026-07-24).
 * Lives in the platform row's specifics — i.e. IN the database it controls —
 * so `local`/`deployed` here scope to whichever DB the reader is connected to
 * (dev/staging/prod). **Absent/false = OFF (the default): automation must be
 * turned on explicitly, so nothing polls a vendor by accident.** Gates only the
 * cron + doorbell; manual "run now" ignores it (start/stop testing always works).
 */
export type ConnectorPollingState = Partial<Record<ConnectorExecution, boolean>>;

/**
 * Door-knock gate per execution (Brian, 2026-09-02): when a vendor webhook
 * rings, the drain runs — unless this combo was told to ignore knocks. A
 * nullable timestamp, not a boolean: null/absent = knocks are processed (the
 * default — a knock is the vendor saying "your document is here"); a time =
 * ignored since then. Independent of `pollingEnabled` (the clock).
 */
export type ConnectorKnocksState = Partial<Record<ConnectorExecution, number | null>>;

/**
 * Why a drain is starting. `cron` honors the sweep gate and the min-interval
 * throttle; `knock` honors neither (the vendor said something is there, and
 * with the sweep off nothing else would fetch it — the run lock serializes,
 * ingest dedupe makes a redundant fetch harmless); `manual` honors neither
 * (a human asked). Whether a knock starts a drain at all is decided by
 * `knocksIgnoredAt` before the drain is kicked.
 */
export type ConnectorDrainTrigger = 'cron' | 'knock' | 'manual';

export type ConnectorVendorStatus = {
  vendor: KnownClearingHouses;
  integrationId: number;
  credentialsValid: boolean | null;
  credentialsLastCheckedAt: number | null;
  /** Named keys (INFRA-11): presence + metadata + rotation audit per key name. */
  keys: Record<string, ConnectorKeyStatus>;
  /** Exchange-type → key-name routing; null until configured via the console. */
  keyRouting: ConnectorKeyRouting | null;
  /**
   * Last error-free inbound drain session (ms; INFRA-6). The poll-lag SLO's
   * anchor — the console renders now − this as the vendor's poll health.
   * Null = no error-free session has ever completed.
   */
  lastProcessedAt: number | null;
  /**
   * Automation poll gate for THIS DB's combos (local/deployed), read from the
   * platform row. The console shows it and toggles it; the drain enforces it.
   */
  pollingEnabled: ConnectorPollingState;
  /** Door-knock gate for THIS DB's combos, read from the platform row (see `ConnectorKnocksState`). */
  knocksIgnoredAt: ConnectorKnocksState;
  /**
   * True when a connector implementation is registered in code. False =
   * an orphaned platform row (decommissioned vendor / manual insert) —
   * surfaced rather than silently hidden; actions are disabled since there
   * is no connector to act through.
   */
  registered: boolean;
};

/**
 * One connector exchange as the ops-console error-detail modal sees it: the
 * row + parsed `meta` (carrying the display-only `dispositionHistory` trail) +
 * the SSOT `retryable` verdict (`isExchangeRetryable`) that drives the Retry
 * button. Fetched on demand via `GET /connector/console?fetch=exchange`.
 */
export type ConnectorConsoleExchange = {
  id: number;
  clinicId: number;
  exchangeId: string;
  integrationId: number;
  type: string;
  disposition: string;
  source: string | null;
  bytes: number | null;
  partnerTransactionId: string | null;
  touched: number | null;
  connectorVendor: string | null;
  meta: Record<string, unknown>;
  retryable: boolean;
  /**
   * A stored payload exists (sidecar row, or legacy `meta.phi.payload`) —
   * gates the modal's Download-payload button (item 8). Server-computed; the
   * UI never decides, same rule as `retryable`.
   */
  hasPayload: boolean;
};

/** A held (or expired) singleton run lock (e.g. transmit-backstop). */
export type ConnectorRunLockInfo = {
  /** 'cron' or the admin's email. */
  holder: string;
  at: number;
  expiresAt: number;
  /**
   * The Bellagio-doorman flag (INFRA-4): a webhook ring that arrives while
   * the inbound drain holds this lock sets it; the drain runs ONE more pass
   * on completion instead of the document waiting a full cron cycle.
   */
  runAgain?: boolean;
};

/** Per-vendor outcome of the credential check (cron or run-now). */
export type ConnectorCredentialVerdict = 'valid' | 'invalid' | 'unreachable';

/**
 * A clinic the ingest funnel MATCHED at unrouted time — denormalized into
 * `meta` at persist so the queue worker sees the clinics the document routed
 * to rather than guessing a clinicId (plan §3e.1). Present only when the row
 * was Unrouted for AMBIGUITY; a genuine no-match row has none. `billingProfileName`/
 * `npi` are populated when a payee profile matched in that clinic, null when the
 * candidate came from a control-number / correlation signal instead.
 */
export type UnroutedCandidateClinic = {
  clinicId: number;
  clinicName: string | null;
  billingProfileName: string | null;
  npi: string | null;
  /**
   * True when the clinic has its OWN active `api` Integration row for the
   * vendor — only then can inbound connector traffic be assigned to it. A
   * clinic with only an SFTP row (or none) is still SHOWN as a matched
   * candidate but cannot be assigned (the assign endpoint enforces this too).
   */
  connectorParticipant: boolean;
};

/** Stable reasons the inbound router or a pinned-clinic assignment can leave queued. */
export type UnroutedRoutingReason =
  | 'no-signal'
  | 'multi-clinic-control-numbers'
  | 'multi-clinic-correlation'
  | 'multi-clinic-payee'
  | 'contradictory-signals'
  | 'uncorroborated'
  | 'patient-ambiguous'
  | 'patient-contradiction'
  | 'no-signal-for-clinic';

/** One document identifier and the clinics whose records matched it. */
export type UnroutedRoutingValueMatch = {
  value: string;
  clinicIds: number[];
};

/** A payee-profile match used by the existing NPI/TIN routing signal. */
export type UnroutedRoutingPayeeMatch = {
  clinicId: number;
  billingProfileId: number;
};

/**
 * Admin-safe result of using the document's patient/account reference to confirm
 * the clinic. The reference and patient identity never leave the router.
 */
export type UnroutedClinicConfirmation = {
  status:
    | 'not-evaluated'
    | 'not-provided'
    | 'confirmed'
    | 'not-confirmed'
    | 'ambiguous'
    | 'contradiction';
  clinicId?: number;
  matches?: number;
};

/** The existing corroboration gate's calculation for one clinic. */
export type UnroutedRoutingGate = {
  controlNumbers: boolean;
  correlation: boolean;
  payee: boolean;
  clinicConfirmation: boolean;
  strongSignalCount: number;
};

/**
 * Exact, PHI-safe evidence behind one Unrouted decision. Signal lookups retain
 * their document values and clinic matches; a short-circuited lookup is stated
 * as not evaluated instead of being misreported as a mismatch.
 */
export type UnroutedRoutingDiagnostics = {
  /** Clinic evaluated by the corroboration gate; null for ambiguity/no-signal parks. */
  clinicId: number | null;
  controlNumbers: {
    evaluated: boolean;
    values: UnroutedRoutingValueMatch[];
    invalidValues: string[];
  };
  correlation: {
    evaluated: boolean;
    values: UnroutedRoutingValueMatch[];
  };
  payee: {
    evaluated: boolean;
    npi: string | null;
    tin: string | null;
    matches: UnroutedRoutingPayeeMatch[];
  };
  clinicConfirmation: UnroutedClinicConfirmation;
  gate?: UnroutedRoutingGate;
};

/** One newest-first item stored under `IntegrationExchange.meta.unrouted`. */
export type UnroutedRoutingDiagnosticEntry = {
  ts: number;
  by: string;
  reason: UnroutedRoutingReason;
  diagnostics: UnroutedRoutingDiagnostics;
};

/** Structured 400 returned when a pinned clinic still cannot be confirmed. */
export type UnroutedAssignmentRefusalResponse = {
  message: string;
  details: UnroutedRoutingDiagnosticEntry;
};

/**
 * The PHI-free `data` payload a disposition change carries on a
 * `BusinessRuleItemType` in `meta.dispositionHistory` — the item's `text` holds
 * the human reason. ONE format for the whole trail: the Unrouted close/reopen
 * audit and connector transmit-failure notes both ride `BusinessRuleItemType`
 * (Brian, 2026-07-23; supersedes the standalone UnroutedDispositionChange).
 */
export type DispositionChangeData = {
  at: number;
  /** Who made the change — an admin email, or 'system' for an automated transition. */
  by: string;
  /** Disposition before → after (e.g. 'unrouted' → 'resolved'). */
  from: string;
  to: string;
  /** Which code path wrote this entry (compact numeric; see exchangeDispositionDataSourceLabel). */
  src?: ExchangeDispositionDataSource;
};

/**
 * One IntegrationExchange row as the admin queue renders it (plan §3e.7 /
 * INFRA-5) — both OPEN (`Unrouted`) and worked (`Resolved`) rows. Display
 * fields are denormalized into `meta` at ingest — the LIST never reads or
 * parses the stored payload. This type is the contract INFRA-5's endpoints
 * implement; the admin UI (INFRA-7) is already built against it.
 */
/**
 * A 277 acknowledgement at a glance — the money columns' counterpart for a document
 * that carries no money. NESTED, while the 835's `trn`/`paymentAmount`/`clpCount` are
 * flat, because those keys predate this contract describing more than one document
 * type; adding a block leaves the 835 shape and its consumers untouched.
 *
 * PHI-free by construction: claim identifiers and STC codes are CLAIM identity.
 */
export type AckSummary = {
  /** Distinct claims the ack speaks about — the `clpCount` of an acknowledgement. */
  claimCount: number;
  /** Our CLM01s (`Invoice.controlNumber`), capped for a list row. */
  claimControlNumbers: string[];
  /** Distinct `STC01-1:STC01-2` pairs, the same key the rejection analytics use. */
  statusCodes: string[];
  /** Statuses the consumer treats as needing a human (Reject / Denied / requiresAction). */
  attention: number;
  totalChargeAmount: number | null;
};

/** The three clinicId-0 queue dispositions the admin queue surface serves. */
export type QueueDisposition = 'unrouted' | 'unsupported' | 'resolved';

/**
 * Server-side bound on one queue's list (INFRA-10.2 item 4). Only one
 * disposition is visible at a time, so the endpoint returns AT MOST this many
 * rows of the requested disposition — never every clinicId-0 row.
 */
export const QUEUE_PAGE_LIMIT = 200;

/** Row counts per queue — the tab badges, without pulling the other queues' rows. */
export type UnroutedQueueTotals = Record<QueueDisposition, number>;

/**
 * The queue list read: ONE disposition's rows (newest first, bounded by
 * `QUEUE_PAGE_LIMIT`) plus all three totals. `disposition` echoes the request
 * so each client cache self-describes for cross-cache row moves.
 */
export type UnroutedQueueList = {
  disposition: QueueDisposition;
  rows: UnroutedExchangeSummary[];
  totals: UnroutedQueueTotals;
};

export type UnroutedExchangeSummary = {
  exchangeId: string;
  /** 'unrouted' (open) or 'resolved' (closed, reversible) — drives the queue view. */
  disposition: string;
  /** The disposition audit trail, newest first — one BusinessRuleItemType per change. */
  history: BusinessRuleItemType[];
  /**
   * The platform vendor when known. Null for INFRA-2 unreadable transmit-queue
   * artifacts (`integrationId = 0` — an unparseable body identifies no vendor).
   */
  vendor: KnownClearingHouses | null;
  docType: string | null;
  receivedAt: number;
  /** Why routing failed — ambiguity is stated, never guessed around. */
  reason: string;
  /** Durable routing decisions, newest first. Legacy rows may have none. */
  routingDiagnostics: UnroutedRoutingDiagnosticEntry[];
  payorName: string | null;
  payeeNpi: string | null;
  payeeTin: string | null;
  /** TRN02 trace number (835). */
  trn: string | null;
  paymentAmount: number | null;
  clpCount: number | null;
  /** Present only on an acknowledgement row, so ordinary rows carry no empty keys. */
  ack?: AckSummary;
  /**
   * Can this row be pushed back through the funnel right now? TRUE only when the
   * vendor can map its doc type AND a dispatcher exists to apply it
   * (`isInboundDocTypeSupported`). Server-computed — the UI renders the button from
   * this and never decides for itself, the same rule as `retryable`.
   * Always false for an `Unrouted` row: those need a clinic, not a parser.
   */
  reprocessable: boolean;
  /**
   * What the document says it is, for a row we could not map. Two independent readings,
   * kept apart on purpose: `vendor` is what the vendor reported at discovery (MIME type,
   * transaction set, guide, functional group), `sniffed` is what the stored BYTES say
   * when read as X12. A disagreement between them is itself a finding.
   */
  descriptors?: { vendor?: Record<string, unknown>; sniffed?: Record<string, unknown> };
  /** Clinics the funnel matched at unrouted time — informs the assign pick. */
  candidates: UnroutedCandidateClinic[];
};
